import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ModalPortal } from "../../components/ModalPortal.js";
import { isTeamScoped } from "./useSwitchTeam.js";

/**
 * Create-team dialog (replaces Clerk's <CreateOrganization>). One field —
 * the team name; the server makes the caller its admin + owner and switches
 * the session to it in the same round trip, so on success we just reset the
 * team-scoped caches and land on /docs.
 */
export function CreateTeamDialog({ onClose }: { onClose: () => void }) {
	const [name, setName] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const qc = useQueryClient();
	const navigate = useNavigate();

	useEffect(() => {
		inputRef.current?.focus();
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape" && !busy) onClose();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose, busy]);

	const submit = async () => {
		const trimmed = name.trim();
		if (!trimmed || busy) return;
		setBusy(true);
		setError(null);
		try {
			const { api } = await import("../../lib/api.js");
			await api.createTeam(trimmed);
			// The server already switched the session — drop team-scoped caches
			// exactly like a switch, then land on the (empty) deck list.
			await qc.cancelQueries({ predicate: isTeamScoped });
			await Promise.all([
				qc.resetQueries({ predicate: isTeamScoped }),
				qc.invalidateQueries({ queryKey: ["me"] }),
			]);
			onClose();
			await navigate({ to: "/docs" });
		} catch (err) {
			setError((err as Error).message || "Couldn't create the team");
			setBusy(false);
		}
	};

	// Portalled to <body>: the header switcher opens this, and the header's
	// backdrop-blur would otherwise become the containing block for the
	// fixed backdrop — clamping it to the header and clipping the panel off
	// the top of the viewport. See ModalPortal.
	return (
		<ModalPortal>
			<div className="mx-modal-backdrop" onClick={busy ? undefined : onClose}>
				<div
					className="mx-modal-panel"
					onClick={(e) => e.stopPropagation()}
					role="dialog"
					aria-modal="true"
					aria-labelledby="create-team-title"
					data-testid="create-team-dialog"
				>
					<span
						aria-hidden="true"
						className="absolute top-0 left-0 h-[3px] w-[14%]"
						style={{ background: "var(--color-mx-electric)" }}
					/>
					<p
						className="mx-eyebrow mb-3"
						style={{ color: "var(--color-mx-electric)" }}
					>
						New team
					</p>
					<h2
						id="create-team-title"
						className="text-2xl font-light tracking-[-0.018em] mb-2"
					>
						Create a team
					</h2>
					<p className="text-sm text-white/60 mb-5 leading-relaxed">
						Decks, brand and billing are all per team. You'll be its admin and
						can invite people from the Team page.
					</p>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							void submit();
						}}
					>
						<input
							ref={inputRef}
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g. Acme Marketing"
							maxLength={80}
							data-testid="create-team-name"
							className="w-full rounded-md border mx-divider bg-white/[0.04] px-3 py-2 text-sm text-white/90 placeholder:text-white/30 focus:outline-none focus:border-[var(--color-mx-electric)]/60"
						/>
						{error && (
							<p
								className="text-sm mt-3"
								style={{ color: "var(--color-mx-magenta)" }}
								data-testid="create-team-error"
							>
								{error}
							</p>
						)}
						<div className="flex justify-end gap-2 pt-5">
							<button
								type="button"
								onClick={onClose}
								disabled={busy}
								className="mx-btn-text text-sm"
							>
								Cancel
							</button>
							<button
								type="submit"
								disabled={busy || !name.trim()}
								data-testid="create-team-submit"
								className="mx-confirm-action text-sm inline-flex items-center gap-1.5 px-3.5 py-1.5 border transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
								style={{
									borderColor: "var(--color-mx-electric)",
									color: "var(--color-mx-electric)",
								}}
							>
								{busy ? "Creating…" : "Create team"}
							</button>
						</div>
					</form>
				</div>
			</div>
		</ModalPortal>
	);
}
